"use client";

import useAnalyticsManager from "@/app/hooks/useAnalyticsManager";
import useNeoEmailSubscriber, { GENERIC_SUBSCRIBE_ERROR } from "@/app/hooks/useNeoEmailSubscriber";
import SocialMediaIcon from "@/app/layouts/standard/footer/socialMediaIcon";
import Button from "@/components/design-system/button";
import { useGetUserQuery } from "@/store/userApi";
import * as EmailValidator from "email-validator";
import { useCallback, useMemo, useState } from "react";
import styles from "./emailSubscribe.module.scss";

const SUBSCRIPTION_TYPES = "Weekly Email;Replenish Email;Shopping Cart Reminder" as const;
const SOURCE = "footer" as const;

export default function EmailSubscribe() {
  const [email, setEmail] = useState("");
  const [userSubscribedToEmail, setUserSubscribedToEmail] = useState<null | boolean>(null);
  const [userSubscribeFailureMessage, setUserSubscribeFailureMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: user } = useGetUserQuery();
  const subscribeToEmailLists = useNeoEmailSubscriber();
  const { dispatchEmailSubscriptionEvent } = useAnalyticsManager();

  const handleError = useCallback((error: unknown) => {
    console.error("Subscription error: ", error);
    setUserSubscribeFailureMessage(GENERIC_SUBSCRIBE_ERROR);
  }, []);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  }, []);

  const subscribe = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (isSubmitting) return;

      setIsSubmitting(true);
      setUserSubscribeFailureMessage("");

      try {
        if (EmailValidator.validate(email) === false) {
          setUserSubscribeFailureMessage("You have entered an invalid email.");
          return;
        }

        const response = await subscribeToEmailLists({
          emailAddress: email,
          subscriptionType: SUBSCRIPTION_TYPES,
          source: SOURCE,
          isLoggedInUser: user?.IsGuestUser === false ? 1 : 0,
        });

        if (response instanceof Error) {
          throw response;
        } else {
          if (response.CallHeader.StatusCode == "SC_OK") {
            setUserSubscribedToEmail(true);
            dispatchEmailSubscriptionEvent(email);
          } else if (response.CallHeader.StatusCode == "SC_WARNING_ALREADY_EXIST") {
            setUserSubscribeFailureMessage("You are already subscribed for Weekly Specials.");
          } else {
            throw new Error(`Unhandled status code: ${response.CallHeader.StatusCode}`);
          }
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, user?.IsGuestUser, subscribeToEmailLists, dispatchEmailSubscriptionEvent, isSubmitting, handleError],
  );

  const isSuccess = useMemo(() => userSubscribedToEmail === true, [userSubscribedToEmail]);

  const buttonText = useMemo(() => (isSubmitting ? "Signing Up..." : "Sign Up"), [isSubmitting]);

  if (isSuccess) {
    return (
      <div className={styles.successContainer}>
        <div className={styles.successIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
          </svg>
        </div>
        <p className={styles.successText}>Thanks for subscribing</p>
        <SocialMediaIcon className={"my-2"} />
      </div>
    );
  }

  return (
    <div className={styles.newsletterSection}>
      <h3 className={styles.title}>Early Access. Exclusive Savings.</h3>
      <form onSubmit={subscribe} className={styles.form}>
        <div className={styles.inputGroup}>
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="you@example.com"
            className={styles.emailInput}
            required
          />
          <Button
            variant="primary"
            color="teal"
            size="large"
            text={buttonText}
            type="submit"
            loading={isSubmitting}
            showLeftIcon={false}
            showRightIcon={false}
            className={styles.submitButton}
          />
        </div>
        {userSubscribeFailureMessage && <p className={styles.errorText}>{userSubscribeFailureMessage}</p>}
      </form>
      <SocialMediaIcon className="w-100" />
    </div>
  );
}
